import { useState, useEffect, useRef } from "react";
import { Modal, Spin } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import { ArrowRightOutlined, LoadingOutlined, SwapOutlined, TableOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { checkTableName } from "../../../../../api/bills";
import { reservationToConfirm } from "./tableReservationNotice";
import { useReservationConfirm } from "./useReservationConfirm";
import { checkNewTableName, hasApiDetail } from "./newTableNameCheck";
import { useNewTableNameInput } from "./useNewTableNameInput";
import { NewTableKeypad } from "./NewTableKeypad";
import { TableCheckLine } from "./TableCheckLine";
import { tableTakenText } from "./tableTakenText";
import { useTableNameLiveCheck } from "./useTableNameLiveCheck";

import { getApiErrorMessage } from "../../../../../utils/translations";
import { useViewport } from "../../../../../hooks/useViewport";
import {
  posModalStyles as modalStyles,
  ModalHeader,
  GhostButton,
  PrimaryButton,
  footerButtonHeight,
} from "../../modalPrimitives";

interface NewTableModalProps {
  open: boolean;
  onClose: () => void;
  /** Вторият аргумент — само за маса на ДРУГ сервитьор (пропът `waiterId`). */
  onSubmit: (tableName: string, waiterId?: number) => void;
  /** Заето име НЕ е грешка (разделяне): проверката при кого е заета масата
   * зависи от целевия сервитьор, който се избира СЛЕД този модал — затова тук
   * не проверяваме, а BE-то отговаря при самото разделяне и FE-то пита
   * „прехвърляне към съществуващата маса?". */
  allowExistingName?: boolean;
  /** Настройката „Предлагане на номер за нова маса": при отваряне полето се
   * попълва с първия свободен номер (`useNewTableNameInput`). Само за „Нова
   * маса" от списъка — разделянето не я подава. */
  suggestName?: boolean;
  /** Масата е на ДРУГ сервитьор, избран в първия модал (`NewTableWaiterModal`
   * през `NewTableFlow`): проверката на името е сред неговите маси и
   * `onSubmit` получава id-то му. Без него — на влезлия. */
  waiterId?: number;
  /** „Назад"/Esc връща към избора на сервитьор, вместо да затваря. */
  onBack?: () => void;
  /** „Към друга маса" от „Раздели": от коя маса се мести — полето е „12 → 5",
   * заглавието и бутонът („Премести на маса 5") са за местене. Само с
   * `allowExistingName`. */
  moveFrom?: string;
}

export const NewTableModal = ({
  open,
  onClose,
  onSubmit,
  allowExistingName = false,
  suggestName = false,
  waiterId,
  onBack,
  moveFrom,
}: NewTableModalProps) => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  // „Масата има резервация — продължаваш ли?" (модал с Отказ/Продължи)
  const { confirmNotice, reservationConfirmModal } = useReservationConfirm();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const {
    tableName,
    isSuggested,
    typeCharacter,
    deleteLast,
    changeFromInput,
    reset: resetTableName,
  } = useNewTableNameInput({ open, suggestName });
  const [error, setError] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const submitRef = useRef<() => void>(() => {});
  const cancelRef = useRef<() => void>(() => {});
  const backRef = useRef<() => void>(() => {});

  const trimmedName = tableName.trim();
  // Заета ли е масата, докато се пише — като в „Смяна на маса". Предложеният
  // номер е свободен по сметка, затова не се пита.
  const liveCheck = useTableNameLiveCheck({
    open,
    name: tableName,
    enabled: !isSuggested,
    // Нова маса на друг сервитьор — неговите маси, както при отварянето.
    waiterId: moveFrom ? undefined : waiterId,
  });
  const isMoving = moveFrom !== undefined;

  // Текстът на „облакът не отговори" е константа в рендера, за да служи и за
  // РАЗПОЗНАВАНЕ: под него се показва редът какво може да направи управител.
  // Така няма второ състояние, което да се чисти на всяко натискане.
  const offlineError = t("ordering.tableCheckOffline", {
    defaultValue:
      "Няма връзка с облака — масата не е създадена. Провери интернета и опитай пак.",
  });

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        submitRef.current();
        return;
      }
      if (e.key === "Escape") {
        // preventDefault = „Esc е консумиран" за глобалната Esc-навигация
        e.preventDefault();
        backRef.current();
        return;
      }
      // Фокус в самото поле: символите и Backspace ги пише input-ът през
      // onChange — без този guard всеки знак би влизал двойно.
      if (e.target instanceof HTMLInputElement) return;
      if (
        e.key.length === 1 &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        /^[\p{L}\p{N} .-]$/u.test(e.key)
      ) {
        typeCharacter(e.key);
        if (error) setError("");
      } else if (e.key === "Backspace") {
        deleteLast();
        if (error) setError("");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, error, typeCharacter, deleteLast]);

  const resetState = () => {
    resetTableName();
    setError("");
    setIsChecking(false);
  };

  const handleCharacter = (character: string) => {
    typeCharacter(character);
    if (error) setError("");
  };

  const handleBackspace = () => {
    deleteLast();
    if (error) setError("");
  };

  submitRef.current = async () => {
    if (!trimmedName) {
      setError(t("ordering.tableNumber"));
      return;
    }
    if (isChecking) return;

    if (allowExistingName) {
      // Разделяне: заетото име е прехвърляне (не се проверява тук), но нова
      // маса с резервация се пита. Провалена проверка не спира разделянето.
      setIsChecking(true);
      let proceed = true;
      try {
        const notice = reservationToConfirm(await checkTableName(trimmedName));
        setIsChecking(false);
        if (notice) proceed = await confirmNotice(trimmedName, notice);
      } catch {
        // при съмнение обслужвай — питането за резервация е удобство
      } finally {
        setIsChecking(false);
      }
      if (!proceed) return;
      setError("");
      onSubmit(trimmedName);
      resetState();
      onClose();
      return;
    }

    // Друг сервитьор → BE проверява името сред НЕГОВИТЕ маси (лична номерация)
    // и отваря масата на него; своята маса не праща `waiterId`.
    // Проверката (заето име, резервация, счупен маршрут) е обща с отварянето
    // на свободна маса от схемата — manage/newTableNameCheck.ts.
    setIsChecking(true);
    const stopChecking = () => setIsChecking(false);
    try {
      const outcome = await checkNewTableName(trimmedName, confirmNotice, stopChecking, waiterId);
      if (outcome.kind === "exists") {
        setError(tableTakenText(t, trimmedName, outcome.takenBy));
        return;
      }
      // „Отказ" на резервацията оставя „Нова маса" отворен.
      if (outcome.kind === "declined") return;
      if (outcome.kind === "failed") {
        // Мълчаливият catch (само console.error) беше докладваният дефект: при
        // спрял интернет въртележката се въртеше до таймаута на ОС-а, после
        // спираше и НЕ СЕ СЛУЧВАШЕ НИЩО — нито грешка, нито маса. Сега полето
        // за грешка казва какво стана, а бутонът остава активен за нов опит.
        setError(
          hasApiDetail(outcome.error)
            ? getApiErrorMessage(outcome.error, offlineError)
            : offlineError,
        );
        return;
      }
      // Счупена проверка (404/5xx) → отваря с предупреждение: до 0.0.269
      // резервът `getBills()` минаваше и масата се отваряше; след махането му
      // екранът започна да отказва (22.09.2026, находка Н7).
      if (outcome.skippedReason !== null) {
        message.warning(
          t("ordering.tableCheckSkipped", { reason: outcome.skippedReason }),
        );
      }
      setError("");
      // Своята маса — само името, както винаги; `waiterId` е само за друг.
      if (waiterId === undefined) onSubmit(trimmedName);
      else onSubmit(trimmedName, waiterId);
      resetState();
      onClose();
    } finally {
      stopChecking();
    }
  };

  cancelRef.current = () => {
    resetState();
    onClose();
  };

  // „Назад"/Esc: към избора на сервитьор, ако модалът е втората стъпка.
  backRef.current = () => {
    if (!onBack) {
      cancelRef.current();
      return;
    }
    resetState();
    onBack();
  };

  // Заета маса от проверката докато се пише; грешката от самото натискане е
  // по-важна. При местенето заетата маса не е отказ — сметките се сливат
  // (BE пита „прехвърли към отворената маса?"), но се казва, както в „Смяна".
  const liveTaken = liveCheck.taken;
  const takenText = liveTaken ? tableTakenText(t, trimmedName, liveTaken.takenBy) : "";
  // При местенето заетата маса е само предупреждение (оранжево) — продуктите
  // отиват в отворената маса; при новата маса е отказ (червено).
  const shownError = error || (isMoving ? "" : takenText);
  const takenNotice = !error && isMoving ? takenText : "";

  const width = isMobile ? undefined : isTablet ? 420 : 460;
  const footerHeight = isNarrow ? footerButtonHeight(true) : 56;

  return (
    <Modal
      open={open}
      onCancel={cancelRef.current}
      footer={null}
      centered
      closable={false}
      // Esc го поема нашият обработчик: след избор на сервитьор връща към
      // него (antd би затворил прозореца).
      keyboard={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div className="bg-pos-card font-sans text-pos-ink">
        <ModalHeader
          icon={isMoving ? <SwapOutlined /> : <TableOutlined />}
          title={isMoving ? t("ordering.moveToTableTitle") : t("ordering.newTable")}
          subtitle={
            isMoving
              ? t("ordering.moveTableSubtitle", { name: moveFrom })
              : t("ordering.tableNumber")
          }
          onClose={cancelRef.current}
          isNarrow={isNarrow}
        />

        <div className={`flex flex-col ${isNarrow ? "gap-3.5 p-4" : "gap-4 px-6 pt-5 pb-6"}`}>
          {/* Number slot — истински input, за да излиза клавиатурата на
              телефона при докосване (САМО тогава — нарочно без autoFocus);
              екранният keypad и глобалният keydown остават другите пътища. */}
          <div
            data-testid="new-table-slot"
            className={`relative flex items-center justify-center rounded-[12px] border font-pos-mono text-[24px] font-bold leading-none tracking-[1px] transition-colors duration-[160ms] ${
              isNarrow ? "h-[52px]" : "h-[60px]"
            } ${
              shownError ? "border-pos-danger-ink" : tableName ? "border-pos-accent" : "border-pos-line2"
            } ${tableName ? "bg-pos-selected-bg text-pos-accent-ink" : "bg-pos-canvas text-pos-muted"}`}
          >
            {/* „Към друга маса": откъде → накъде, като в „Смяна на маса". */}
            {isMoving && (
              <>
                <span className="max-w-[35%] shrink-0 truncate text-[22px] tracking-normal text-pos-soft2">
                  {moveFrom}
                </span>
                <ArrowRightOutlined className="mx-3 shrink-0 text-[14px] text-pos-muted" />
              </>
            )}
            <input
              value={tableName}
              onChange={(e) => {
                // същият филтър като глобалния keydown — букви/цифри/интервал/./-
                // (в hook-а); върху предложен номер написаното го заменя
                changeFromInput(e.target.value, e.nativeEvent);
                if (error) setError("");
              }}
              // Предложеният номер се маркира целия — телефонната клавиатура
              // го заменя, вместо да пише след него.
              onFocus={(e) => {
                if (isSuggested) e.currentTarget.select();
              }}
              placeholder="—"
              // При местенето полето е широко колкото номера — „12 → 5" стои в средата.
              size={isMoving ? Math.max(tableName.length, 1) : undefined}
              className={`h-full min-w-0 border-0 bg-transparent text-center outline-none [font:inherit] text-inherit tracking-[inherit] placeholder:text-pos-muted ${
                isMoving ? "w-auto max-w-[45%] text-[28px]" : "w-full"
              }`}
            />
            {(isChecking || liveCheck.isChecking) && (
              <span className="absolute top-1/2 right-3.5 -translate-y-1/2">
                <Spin indicator={<LoadingOutlined spin className="text-[18px] text-pos-accent" />} />
              </span>
            )}
          </div>

          {/* Проверката — 1:1 с „Смяна на маса" (`TableCheckLine`). */}
          {shownError ? (
            <TableCheckLine tone="error">
              {shownError}
              {shownError === offlineError && (
                // Какво да НАПРАВИ човекът, а не само какво е станало:
                // автоматичното превключване може и да не е решило, а
                // управителят има ръчния път от лентата горе.
                <div className="mt-0.5 opacity-85">
                  {t("ordering.tableCheckOfflineHint", {
                    defaultValue:
                      "Ако интернетът е спрял, управител може да превключи обекта на работа срещу касата.",
                  })}
                </div>
              )}
            </TableCheckLine>
          ) : takenNotice ? (
            <TableCheckLine tone="warning">{takenNotice}</TableCheckLine>
          ) : liveCheck.failed ? (
            <TableCheckLine tone="warning">{t("ordering.tableCheckLiveFailed")}</TableCheckLine>
          ) : isSuggested ? (
            <TableCheckLine tone="hint">{t("ordering.newTableSuggestedHint")}</TableCheckLine>
          ) : null}

          <NewTableKeypad
            onCharacter={handleCharacter}
            onBackspace={handleBackspace}
            backspaceDisabled={tableName.length === 0}
            isNarrow={isNarrow}
          />

          {/* Footer actions — „Назад" тесен (1:3) на всички размери */}
          <div className="mt-1 grid grid-cols-[1fr_3fr] gap-2.5">
            <GhostButton
              label={t("common.back")}
              onClick={backRef.current}
              shortcut={isDesktop ? "Esc" : undefined}
              height={footerHeight}
            />
            <PrimaryButton
              label={
                isMoving
                  ? trimmedName
                    ? t("ordering.moveToTableButton", { name: trimmedName })
                    : t("ordering.moveTableButton")
                  : t("common.ok")
              }
              onClick={() => submitRef.current()}
              // Заета маса е отказ при новата маса; при местенето — сливане.
              disabled={!trimmedName || isChecking || (!isMoving && !!liveTaken)}
              shortcut={isDesktop ? "⏎" : undefined}
              height={footerHeight}
            />
          </div>
        </div>
      </div>
      {reservationConfirmModal}
    </Modal>
  );
};
